import React from "react";
import { CommunityProps } from "../../types";
import { GroupAdd, Rounded } from "../../assets/images";
import { useMutation } from "@apollo/client";
import { JOIN_COMMUNITY, LEAVE_COMMUNITY } from "../../apollo/operations";
import { ipfsGatewayImage } from "../../utils/ipfsGatewayImage";
import { CiUser } from "react-icons/ci";
import { Shimmer, SkeletonElement } from "..";

type CommunityHeaderProps = {
  community: CommunityProps | undefined;
  refetch?: () => void;
  loadinng?: boolean;
};

const CommunityHeader: React.FC<CommunityHeaderProps> = ({
  community,
  refetch,
  loadinng,
}) => {
  const userID = window.localStorage.getItem("user");

  const findUser = community?.members?.find((user) => user.id === userID);

  const [leaveCommunity, { loading }] = useMutation(LEAVE_COMMUNITY, {
    onCompleted(data) {
      console.log(data);
      if (refetch) {
        refetch();
      }
    },
    onError: (err) => {
      console.log(err);
      const errMsg = err.graphQLErrors[0]?.extensions?.originalError;
      console.log(errMsg);
    },
  });

  const [joinCommunity, { loading: loadData }] = useMutation(JOIN_COMMUNITY, {
    onCompleted(data) {
      console.log(data);
      if (refetch) {
        refetch();
      }
    },
    onError: (err) => {
      console.log(err);
      const errMsg = err.graphQLErrors[0]?.extensions?.originalError;
      console.log(errMsg);
    },
  });

  const handleLeave = (e: any) => {
    if (findUser) {
      leaveCommunity({
        variables: {
          data: {
            member: {
              id: userID,
            },
          },
          where: {
            id: community?.id,
          },
        },
      }).then(() => {});
    }
  };

  const handleJoin = (e: any) => {
    e.preventDefault();

    if (findUser === undefined) {
      joinCommunity({
        variables: {
          data: {
            member: {
              id: userID,
            },
          },
          where: {
            id: community?.id,
          },
        },
      }).then(() => {});
    }
  };

  let images: (string | null)[] = Array(6).fill("");

  if (community?.members) {
    community.members.slice(0, 6).forEach((member, index) => {
      const profileImage = member?.ecobookProfile?.profileImage ?? null;
      if (profileImage) {
        images[index] = profileImage;
      }
    });
  }

  // Access individual images:
  let _image1 = images[0];
  let image2 = images[1];
  let image3 = images[2];
  let image4 = images[3];
  let image5 = images[4];
  let image6 = images[5];

  const totalMembers = community?.members! && community?.members?.length;

  const renderProfileImage = (image: string | null, defaultMargin: number) => {
    return image ? (
      <img
        alt=""
        src={ipfsGatewayImage(image)}
        className={`w-10 h-10 rounded-full ml-[${defaultMargin}px]`}
      />
    ) : (
      <div
        className={`w-10 h-10 ml-[-17px] bg-slate-100 flex items-center justify-center rounded-full`}
      >
        <CiUser size={20} />
      </div>
    );
  };

  const profileImages = [
    { image: image3, margin: -27 },
    { image: image4, margin: -27 },
    { image: image5, margin: -27 },
    { image: image6, margin: -27 },
  ];

  // const findMember = community?.members.find((member) => member.id === userID);

  //   console.log(findMember);

  return (
    <div className="flex flex-col px-4 lg:px-0 gap-4">
      {loadinng && (
        <div className="relative overflow-hidden w-[60%]">
          <SkeletonElement type="title" />
          <Shimmer />
        </div>
      )}
      {!loadinng && (
        <div className="font-Poppins text-base lg:text-[34px] text-[#000] font-bold">
          {community?.name} Community
        </div>
      )}
      <div className="h-10 flex justify-between">
        <div className="flex items-center gap-7">
          <div className="flex">
            <div className="w-10 h-10 rounded-full">
              {_image1 ? (
                <img
                  alt=""
                  src={ipfsGatewayImage(_image1!)}
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 bg-slate-100 flex items-center justify-center rounded-full">
                  <CiUser size={20} />
                </div>
              )}
            </div>

            {community?.members &&
              totalMembers > 1 &&
              (image2 ? (
                <img
                  alt=""
                  src={ipfsGatewayImage(image2!)}
                  className="w-10 h-10 rounded-full ml-[-27px]"
                />
              ) : (
                <div className="w-10 ml-[-17px] h-10 bg-slate-100 flex items-center justify-center rounded-full">
                  <CiUser size={20} />
                </div>
              ))}
            {community?.members &&
              totalMembers > 2 &&
              profileImages.map((profile, index) => {
                if (totalMembers > index + 2) {
                  return (
                    <React.Fragment key={index}>
                      {renderProfileImage(profile.image, profile.margin)}
                    </React.Fragment>
                  );
                }
                return null;
              })}
          </div>

          <h4 className="font-Poppins text-black text-sm font-medium">
            {totalMembers}
            <span className="font-normal">
              {` Member${totalMembers > 1 ? "s" : ""}`}
            </span>
          </h4>
        </div>
        <div className="flex gap-3">
          {findUser === undefined ? (
            <button
              disabled={loadData || loadinng}
              className="bg-midGreen flex justify-center items-center w-14 h-8 text-white font-Poppins hover:opacity-90 hover:ease-linear hover:duration-300 text-sm font-light rounded-md shadow active:w-12 active:mt-[3px] active:h-7 active:mx-[2px] active:duration-100"
              type="button"
              onClick={handleJoin}
            >
              {!findUser && "Join"}
              {loadData ? (
                <div className="flex items-center justify-center">
                  <img src={Rounded} alt="" className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                ""
              )}
            </button>
          ) : (
            <button
              disabled={loading || loadinng}
              className="bg-midGreen flex justify-center items-center w-14 max-w-20 h-8 text-white font-Poppins hover:opacity-90 hover:ease-linear hover:duration-300 text-sm font-light rounded-md shadow active:w-12 active:mt-[3px] active:h-7 active:mx-[2px] active:duration-100"
              type="button"
              onClick={handleLeave}
            >
              {findUser && "Leave"}
              {loading ? (
                <div className="flex items-center justify-center">
                  <img src={Rounded} alt="" className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                ""
              )}
            </button>
          )}
          <div className="bg-midGreen w-8 h-8 items-center justify-center flex rounded shadow cursor-pointer">
            <img alt="" src={GroupAdd} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityHeader;
